import { Component, type ErrorInfo, type ReactNode } from 'react';
import { i18n } from '../i18n';

const RELOAD_KEY = 'etap_chunk_reload_at';

/**
 * Чанк страницы не догрузился: сеть моргнула или после деплоя сервис-воркер
 * уже выкинул старые файлы, а открытая вкладка ещё ссылается на них.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|Importing a module script failed|Loading chunk|Unable to preload CSS/i.test(message);
}

/**
 * Перезагружает страницу, но не чаще раза в 30 с — иначе при лежащей сети
 * ушли бы в бесконечный цикл. false — перезагрузка уже была, надо показать ошибку.
 */
export function reloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < 30_000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // без sessionStorage цикл не отследить — лучше показать ошибку
    return false;
  }
  window.location.reload();
  return true;
}

/** Кэш оболочки мог застрять на битой версии; сервис-воркер с push-подпиской не трогаем. */
export async function clearCachesAndReload() {
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch {
    // caches может не быть (http, старый браузер) — просто перезагрузимся
  }
  window.location.reload();
}

interface Props {
  children: ReactNode;
  /** Смена ключа (например, пути) сбрасывает ошибку. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/** Вместо белого экрана при падении рендера — экран с кнопкой «Обновить». */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('App crashed:', error, info.componentStack);
    if (isChunkLoadError(error)) reloadOnce();
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="app-error">
        <h2>{i18n.t('errors.loadTitle')}</h2>
        <p>{i18n.t('errors.loadHint')}</p>
        <button type="button" className="app-error-reload" onClick={() => void clearCachesAndReload()}>
          {i18n.t('errors.reload')}
        </button>
      </div>
    );
  }
}
